// Plan 06-05 Task 3 — CofrePage.
//
// Lista de pedidos de break-glass + 4 modais (novo / sign / reconstruct /
// revoke). Plaintext recebido em /reconstruct é exibido em <details>
// collapsed + botões copy/download; NÃO persiste em storage. Após
// copy/download, variável local é resetada (D-146 best-effort).

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Field, Input, Modal, Textarea } from '@aions/ui-primitives';
import { apiFetch } from '../lib/api-client';
import { useSession } from '../contexts/SessionContext';
import {
  CofreRequestCard,
  type CofreRequest,
  type CofreStateKind,
} from '../components/CofreRequestCard';
import { SolicitacoesAcessoPanel } from '../components/SolicitacoesAcessoPanel';

/**
 * 🔴 O ERRO DO SERVIDOR, e não "Erro inesperado" (relato do operador, 2026-08-19).
 *
 * Relato literal: _"quando eu inicio pedido ao cofre ele gera Erro inesperado. Tente
 * novamente"_. A API estava CERTA — devolvia
 * `400 {"error":"invalid_payload","detail":"motivo must be >= 50 chars"}` — e os CINCO
 * sítios desta tela descartavam isso e imprimiam uma frase genérica.
 *
 * Consequência medida: o operador digitou um motivo curto, viu "erro inesperado", e o
 * pedido nunca foi criado — o que apareceu depois como "o card não está sendo gerado".
 * Dois sintomas, um defeito.
 *
 * É a família do "fallback plausível esconde ausência" (§CLAUDE.md) na camada de UI: uma
 * mensagem plausível no lugar de uma específica e ACIONÁVEL. "Erro inesperado. Tente
 * novamente" instrui a repetir exatamente o que não vai funcionar.
 *
 * Códigos conhecidos ganham copy própria; o resto cai no genérico — mas com o código
 * anexado, para o desconhecido ser investigável em vez de mudo.
 */
async function erroDoServidor(
  resp: Response,
  // Tipo do `t` do react-i18next, não uma assinatura própria: declarar `(k, o) => string`
  // colide com o `TFunction` real (overloads + generics de chave) e o `tsc` reprova.
  t: ReturnType<typeof useTranslation>['t'],
): Promise<string> {
  const corpo = (await resp.json().catch(() => ({}))) as {
    error?: string;
    reason?: string;
    detail?: string;
  };
  // ⚠️ `reason` VEM PRIMEIRO, e não é preferência de estilo. Dois 403 do cofre chegam como
  // `{error:'forbidden', reason:'denunciado_no_proprio_caso'}` — ler só o `error` daria a
  // copy genérica ("você não pode praticar este ato"), que é o MESMO defeito que esta
  // função existe para consertar: mensagem plausível no lugar da acionável.
  const codigo = corpo.reason ?? corpo.error ?? `http_${String(resp.status)}`;
  return t(`cofre.erros.${codigo}`, {
    defaultValue: t('common.error_unknown') + ` (${codigo})`,
  });
}

type Modal =
  | { kind: 'none' }
  | { kind: 'new' }
  | { kind: 'sign'; requestId: string }
  | { kind: 'reconstruct'; requestId: string }
  | { kind: 'revoke'; requestId: string };

interface RawRequest {
  requestId: string;
  denunciaId: string;
  motivoHash: string;
  expiresAt: number;
  stateKind: CofreStateKind;
  signerCount: number;
  solicitanteUlid: string;
  solicitanteEmail?: string | null;
}

export function CofrePage(): JSX.Element {
  const { t } = useTranslation();
  const { session } = useSession();
  const currentUserId = session?.user.id ?? '';

  const [requests, setRequests] = useState<CofreRequest[]>([]);
  /** Roster de custódia — quem pode assinar o quórum. Vazio até a 1ª leitura. */
  const [holders, setHolders] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<Modal>({ kind: 'none' });
  const [submitting, setSubmitting] = useState(false);
  const [plaintext, setPlaintext] = useState<string | null>(null);

  // Form state for "new request" modal.
  const [newDenunciaId, setNewDenunciaId] = useState('');
  const [newMotivo, setNewMotivo] = useState('');

  // Form state for "sign" modal.
  const [signTotp, setSignTotp] = useState('');

  const reload = useCallback(async (): Promise<void> => {
    try {
      const resp = await apiFetch('/api/cofre/requests');
      if (!resp.ok) {
        setError(await erroDoServidor(resp, t));
        return;
      }
      const data = (await resp.json()) as { requests?: RawRequest[]; cofreHolders?: string[] };
      setRequests((data.requests ?? []) as CofreRequest[]);
      setHolders(data.cofreHolders ?? []);
      setError(null);
    } catch {
      setError(t('common.error_network'));
    }
  }, [t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  function closeModal(): void {
    setModal({ kind: 'none' });
    setNewDenunciaId('');
    setNewMotivo('');
    setSignTotp('');
  }

  async function submitNewRequest(): Promise<void> {
    if (newMotivo.length < 50) {
      setError(t('cofre.modal.motivo_too_short'));
      return;
    }
    setSubmitting(true);
    try {
      const resp = await apiFetch('/api/cofre/access-request', {
        method: 'POST',
        body: JSON.stringify({ denunciaId: newDenunciaId, motivo: newMotivo }),
      });
      if (!resp.ok) {
        setError(await erroDoServidor(resp, t));
        return;
      }
      closeModal();
      await reload();
    } catch {
      setError(t('common.error_network'));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitSign(requestId: string): Promise<void> {
    setSubmitting(true);
    try {
      const resp = await apiFetch('/api/cofre/sign', {
        method: 'POST',
        body: JSON.stringify({ requestId, totp: signTotp }),
      });
      if (!resp.ok) {
        setError(await erroDoServidor(resp, t));
        return;
      }
      closeModal();
      await reload();
    } catch {
      setError(t('common.error_network'));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitReconstruct(requestId: string): Promise<void> {
    // Custódia única (ADR-0015): sem shares — o Worker usa ESCROW_PRIVATE_JWK
    // direto. A defesa é o quórum procedural 2-de-3 (estado AssinaturasReunidas).
    setSubmitting(true);
    try {
      const resp = await apiFetch('/api/cofre/reconstruct', {
        method: 'POST',
        body: JSON.stringify({ requestId }),
      });
      if (!resp.ok) {
        setError(await erroDoServidor(resp, t));
        return;
      }
      const data = (await resp.json()) as { ok: boolean; plaintext?: string };
      if (data.plaintext) {
        setPlaintext(data.plaintext);
      }
      closeModal();
      await reload();
    } catch {
      setError(t('common.error_network'));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitRevoke(requestId: string): Promise<void> {
    setSubmitting(true);
    try {
      const resp = await apiFetch(`/api/cofre/requests/${requestId}/revoke`, {
        method: 'POST',
        body: JSON.stringify({ motivo: 'manual_revoke' }),
      });
      if (!resp.ok) {
        setError(await erroDoServidor(resp, t));
        return;
      }
      closeModal();
      await reload();
    } catch {
      setError(t('common.error_network'));
    } finally {
      setSubmitting(false);
    }
  }

  function copyPlaintext(): void {
    if (!plaintext) return;
    void navigator.clipboard.writeText(plaintext);
  }

  function downloadPlaintext(): void {
    if (!plaintext) return;
    const blob = new Blob([plaintext], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cofre-plaintext-${String(Date.now())}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function dismissPlaintext(): void {
    setPlaintext(null);
  }

  // 🔴 O `admin_ti` PURO entra nesta página, mas SÓ para os pedidos de acesso.
  //
  // A fila mora aqui por decisão do operador (23/08), e o `admin_ti` é obrigatório numa das
  // duas cadeiras da autorização — logo ele TEM de alcançar a página. Mas o break-glass é
  // gateado no servidor por `requireRole(['cofre_holder','admin_master'])` (cofre/index.ts):
  // renderizar os botões dele para o `admin_ti` seria o "link visível + acesso negado é o
  // pior par possível" que o `App.tsx` condena — parece bug de permissão do usuário quando é
  // decisão do sistema. Então a página se ADAPTA ao papel em vez de oferecer o que o
  // servidor vai recusar, e diz por escrito o que não está ali.
  const podeBreakGlass = (session?.user.roles ?? []).some(
    (r) => r === 'cofre_holder' || r === 'admin_master',
  );
  if (!podeBreakGlass) {
    return (
      <section>
        <h1>{t('cofre.title')}</h1>
        <SolicitacoesAcessoPanel />
        <Alert variant="info" role="status">
          {t('cofre.sem_break_glass')}
        </Alert>
      </section>
    );
  }

  return (
    <section>
      <h1>{t('cofre.title')}</h1>

      {/* PEDIDOS A SEREM LIBERADOS — mora aqui por decisão do operador (23/08).
          NÃO é break-glass: autorizar não toca o escrow, e o wrap continua nascendo no
          browser de quem tem a DEK (ADR-0002). Vem ANTES do aviso de break-glass porque é
          a tarefa de rotina da tela; o cofre é a excepcional. */}
      <SolicitacoesAcessoPanel />

      <Alert variant="warning" role="status" style={{ marginBottom: 16 }}>
        {t('cofre.warning')}
      </Alert>

      {error && (
        <Alert variant="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {/* 🔴 QUEM PODE ASSINAR (relato do operador, 2026-08-19): "ele solicitou duas
          assinaturas (não sei de quem são e quem deve assinar)". O quórum do ADR-0015 é
          entre `cofre_holder`, e esse papel não aparecia em lugar nenhum da interface.
          Com MENOS DE 2 o quórum é inalcançável — e a tela precisa dizer isso, senão o
          pedido fica "aguardando 2 assinaturas" para sempre, sem explicação. */}
      {holders.length > 0 && (
        <p data-testid="cofre-holders" style={{ color: 'var(--text-secondary)' }}>
          {t('cofre.quem_assina')} <strong>{holders.join(', ')}</strong>
          {holders.length < 2 && ` — ${t('cofre.quorum_inalcancavel')}`}
        </p>
      )}

      <Button onClick={() => setModal({ kind: 'new' })}>{t('cofre.button.new_request')}</Button>

      <div className="cofre-grid">
        {requests.length === 0 ? (
          <p>{t('cofre.empty')}</p>
        ) : (
          requests.map((r) => (
            <CofreRequestCard
              key={r.requestId}
              request={r}
              currentUserId={currentUserId}
              onSign={() => setModal({ kind: 'sign', requestId: r.requestId })}
              onReconstruct={() => setModal({ kind: 'reconstruct', requestId: r.requestId })}
              onRevoke={() => setModal({ kind: 'revoke', requestId: r.requestId })}
            />
          ))
        )}
      </div>

      {plaintext && (
        <div className="plaintext-reveal">
          <details>
            <summary>{t('cofre.plaintext_revealed')}</summary>
            <p className="cf-field__help">{t('cofre.plaintext_warning')}</p>
            <pre>{plaintext}</pre>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <Button variant="ghost" size="sm" onClick={copyPlaintext}>
                {t('common.copy')}
              </Button>
              <Button variant="ghost" size="sm" onClick={downloadPlaintext}>
                {t('common.download')}
              </Button>
              <Button variant="danger" size="sm" onClick={dismissPlaintext}>
                {t('common.close')}
              </Button>
            </div>
          </details>
        </div>
      )}

      {modal.kind === 'new' && (
        <Modal onClose={closeModal} title={t('cofre.modal.new_title')} titleId="modal-new-title">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitNewRequest();
            }}
          >
            <Field label={t('cofre.modal.denuncia_id_label')}>
              {({ id }) => (
                <Input
                  id={id}
                  value={newDenunciaId}
                  onChange={(e) => setNewDenunciaId(e.target.value)}
                  required
                />
              )}
            </Field>
            <Field label={t('cofre.modal.motivo_label')}>
              {({ id }) => (
                <Textarea
                  id={id}
                  value={newMotivo}
                  onChange={(e) => setNewMotivo(e.target.value)}
                  placeholder={t('cofre.modal.motivo_placeholder')}
                  minLength={50}
                  required
                  rows={4}
                />
              )}
            </Field>
            <div className="cf-modal__actions">
              <Button variant="ghost" onClick={closeModal}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" loading={submitting}>
                {submitting ? t('common.saving') : t('common.confirm')}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {modal.kind === 'sign' && (
        <Modal onClose={closeModal} title={t('cofre.modal.sign_title')} titleId="modal-sign-title">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitSign(modal.requestId);
            }}
          >
            <Field label={t('cofre.modal.totp_label')}>
              {({ id }) => (
                <Input
                  id={id}
                  type="text"
                  inputMode="numeric"
                  mono
                  value={signTotp}
                  onChange={(e) => setSignTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                />
              )}
            </Field>
            <div className="cf-modal__actions">
              <Button variant="ghost" onClick={closeModal}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" loading={submitting}>
                {submitting ? t('common.saving') : t('common.confirm')}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {modal.kind === 'reconstruct' && (
        <Modal
          onClose={closeModal}
          title={t('cofre.modal.reconstruct_title')}
          titleId="modal-reconstruct-title"
          actions={
            <>
              <Button variant="ghost" onClick={closeModal}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={() => void submitReconstruct(modal.requestId)}
                loading={submitting}
              >
                {submitting ? t('common.saving') : t('common.confirm')}
              </Button>
            </>
          }
        >
          <p>{t('cofre.modal.reconstruct_confirm')}</p>
        </Modal>
      )}

      {modal.kind === 'revoke' && (
        <Modal
          onClose={closeModal}
          title={t('cofre.modal.revoke_title')}
          titleId="modal-revoke-title"
          actions={
            <>
              <Button variant="ghost" onClick={closeModal}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={() => void submitRevoke(modal.requestId)}
                loading={submitting}
              >
                {submitting ? t('common.saving') : t('common.confirm')}
              </Button>
            </>
          }
        >
          <p>{t('cofre.modal.revoke_confirm')}</p>
        </Modal>
      )}
    </section>
  );
}
